import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISpecialGames } from '@GFTypes/GameStoreTypes/SpinOutcome';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EntryCondition } from '@Managers/StateMachine/EntryCondition';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { GameScene } from '..';
import { isLastSpecialGamesSpin } from './SpecialGamesHelper';

export class Outro extends GameComponent {
    private currentSpecialGames: ISpecialGames;
    private readonly outro?: SpineWrapper;
    private readonly info?: SpineWrapper;
    private readonly specialGamesOutroLeaveCondition = new LeaveCondition<EGameStates>('waitForOutroAnimation');
    private readonly specialGamesSummaryLeaveCondition = new LeaveCondition<EGameStates>('waitForSummaryToEnd');

    constructor(
        public readonly scene: GameScene,
        outro?: SpineWrapper,
        info?: SpineWrapper
    ) {
        super(scene);
        const hasOutroAnimations = outro?.eventManager.hasGameEventAnimations([EGameEvents.SPECIAL_GAMES_OUTRO], true);
        const hasInfoAnimations = info?.eventManager.hasGameEventAnimations(
            [EGameEvents.SPECIAL_GAMES_SUMMARY_SHOW, EGameEvents.SPECIAL_GAMES_SUMMARY_HIDE],
            true
        );
        if (hasOutroAnimations) this.outro = outro;
        if (hasInfoAnimations) this.info = info;
        this.createSubStates();
        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_OUTRO_STATE, this.onPreSpecialGamesOutroState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_OUTRO_STATE, this.onSpecialGamesOutroState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_SUMMARY_STATE, this.onSpecialGamesSummaryState, this);
    }

    private createSubStates(): void {
        const entryCondition = new EntryCondition<EGameStates>('checkIfIsStartOfSpecialGames', () => {
            if (isLastSpecialGamesSpin(this.gameStore)) return true;
            return false;
        });
        const hasOutroSpine = new EntryCondition<EGameStates>('hasOutroSpine', () => !!this.outro);
        const hasInfoSpine = new EntryCondition<EGameStates>('hasInfoSpine', () => !!this.info);

        const subStates = {
            [EGameStates.PRE_SPECIAL_GAMES_OUTRO_STATE]: {
                entryConditions: [entryCondition],
                leaveConditions: []
            },
            [EGameStates.SPECIAL_GAMES_OUTRO_STATE]: {
                entryConditions: [entryCondition, hasOutroSpine],
                leaveConditions: [this.specialGamesOutroLeaveCondition]
            },
            [EGameStates.SPECIAL_GAMES_SUMMARY_STATE]: {
                entryConditions: [entryCondition, hasInfoSpine],
                leaveConditions: [this.specialGamesSummaryLeaveCondition]
            },
            [EGameStates.POST_SPECIAL_GAMES_OUTRO_STATE]: {
                entryConditions: [entryCondition],
                leaveConditions: []
            }
        };

        StateManager.assignSubStates(EGameStates.POST_WIN_FEATURES_STATE, subStates);
    }

    private onPreSpecialGamesOutroState(): void {
        const { specialGames } = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        if (specialGames) this.currentSpecialGames = specialGames;
    }

    private async onSpecialGamesOutroState(): Promise<void> {
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_OUTRO);
        await this.outro?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_OUTRO, 'complete').promise;
        this.specialGamesOutroLeaveCondition.resolve();
    }

    private async onSpecialGamesSummaryState(): Promise<void> {
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_SUMMARY_SHOW);
        await this.info?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_SUMMARY_SHOW, 'complete').promise;
        await EventsCenter.waitForEvent(EGameEvents.CONTINUE);
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_SUMMARY_HIDE);
        await this.info?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_SUMMARY_HIDE, 'complete').promise;
        this.specialGamesSummaryLeaveCondition.resolve();
    }
}
